// track/components/DuplicateReviewModal.tsx
import React from 'react';
import { Transaction } from '../types';

interface PotentialDuplicate {
  newTransaction: any;
  existingTransaction: Transaction;
  include: boolean;
  index: number;
}

interface DuplicateReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  potentialDuplicates: PotentialDuplicate[];
  nonDuplicateTransactionsCount: number;
  onConfirm: () => void;
  onToggleInclusion: (index: number) => void;
  onToggleAll: (include: boolean) => void;
  isLoading: boolean;
}

const DuplicateReviewModal: React.FC<DuplicateReviewModalProps> = ({
  isOpen,
  onClose,
  potentialDuplicates,
  nonDuplicateTransactionsCount,
  onConfirm,
  onToggleInclusion,
  onToggleAll,
  isLoading,
}) => {
  if (!isOpen) {
    return null;
  }
  
  const selectedDuplicatesCount = potentialDuplicates.filter(d => d.include).length;
  const totalToImport = nonDuplicateTransactionsCount + selectedDuplicatesCount;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Review Potential Duplicates</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-start">
                <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                </div>
                <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Potential Duplicates Detected</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                        <p>We found {potentialDuplicates.length} potential duplicate transaction{potentialDuplicates.length > 1 ? 's' : ''}.</p>
                        <p className="mt-1">Review each potential duplicate below and check the box to include it in the import.</p>
                    </div>
                </div>
            </div>
        </div>

        {potentialDuplicates.length > 0 && (
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={() => onToggleAll(true)} className="text-sm text-blue-600 hover:text-blue-800">Select All</button>
              <button onClick={() => onToggleAll(false)} className="text-sm text-blue-600 hover:text-blue-800">Deselect All</button>
            </div>
            <div className="text-sm text-gray-600">{selectedDuplicatesCount} of {potentialDuplicates.length} selected</div>
          </div>
        )}

        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto">
            <div className="space-y-4">
              {potentialDuplicates.map((duplicate, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 pt-1">
                      <input
                        type="checkbox"
                        checked={duplicate.include}
                        onChange={() => onToggleInclusion(index)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-900">
                          Potential Duplicate #{index + 1}
                          <span className="text-gray-500 font-normal"> (Row {duplicate.newTransaction.rowNumber})</span>
                        </h4>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${duplicate.include ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {duplicate.include ? 'Will Import' : 'Will Skip'}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* New Transaction */}
                        <div className="bg-blue-50 p-3 rounded-md">
                          <h5 className="text-xs font-semibold text-blue-800 mb-2 uppercase tracking-wide">New Transaction (CSV)</h5>
                          <div className="space-y-1 text-sm">
                            <div><strong>Date:</strong> {new Date(duplicate.newTransaction.date).toLocaleDateString()}</div>
                            <div><strong>Description:</strong> {duplicate.newTransaction.description}</div>
                            <div><strong>Amount:</strong> <span className={duplicate.newTransaction.amount > 0 ? 'text-green-600' : 'text-red-600'}>£{Math.abs(duplicate.newTransaction.amount).toFixed(2)}</span></div>
                          </div>
                        </div>
                        {/* Existing Transaction */}
                        <div className="bg-gray-50 p-3 rounded-md">
                          <h5 className="text-xs font-semibold text-gray-800 mb-2 uppercase tracking-wide">Existing Transaction</h5>
                          <div className="space-y-1 text-sm">
                            <div><strong>Date:</strong> {new Date(duplicate.existingTransaction.date).toLocaleDateString()}</div>
                            <div><strong>Description:</strong> {duplicate.existingTransaction.description}</div>
                            <div><strong>Amount:</strong> <span className={duplicate.existingTransaction.amount > 0 ? 'text-green-600' : 'text-red-600'}>£{Math.abs(duplicate.existingTransaction.amount).toFixed(2)}</span></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <p>{totalToImport} transaction{totalToImport !== 1 ? 's' : ''} will be imported.</p>
            </div>
            <div className="flex space-x-3">
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button
                onClick={onConfirm}
                disabled={totalToImport === 0 || isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              >
                {isLoading ? 'Importing...' : `Import ${totalToImport} Transaction${totalToImport !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateReviewModal;